// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {Fragment, useContext, useEffect, useMemo, useState}
    from 'react';
import {Settings_context} from '../settings_ctx';
import {Install_btn} from '../ui/install_btn';
import Arrow_img from '../img/homepage/how_it_works_arrow.svg';
import {Editor} from '../../../shared/editor/editor';
import {Button} from '../../../shared/button';
import {Agreement_message} from './agreement_message';
import {get_img_url} from './utils';
import cn from 'classnames';
import styles from './how_it_works_2.module.less';

interface i_how_it_works_2_card_props {
    img_src: string;
    title: string | React.ReactNode;
    description: string | React.ReactNode;
    reversed?: boolean;
}

const cards_data: i_how_it_works_2_card_props[] = [
    {
        img_src: get_img_url('install_and_register.png'),
        title: '1. Install & Register',
        description: 'Get the free VPN app or extension for Windows, Mac, '
            +'Chrome, Android, iOS, and more. Create your free account to '
            +'unlock access.',
        reversed: true,
    },
    {
        img_src: get_img_url('location.png'),
        title: '2. Pick a Location',
        description: 'Choose from 190+ VPN locations. Connect instantly to '
            +'countries like the US, UK, Japan, and many more.',
    },
    {
        img_src: get_img_url('watch.png'),
        title: '3. Browse',
        description: 'Enjoy global content! Experience high speed browsing '
            +'without throttling or data caps.',
        reversed: true,
    },
];

function process_html_classes(html: string, process_class_name: (
class_name: string)=>string): string
{
    return html.replace(/class\s*=\s*["']([^"']+)["']/g, (match, list)=>{
        let new_list = list.split(/\s+/).map(process_class_name).join(' ');
        return `class="${new_list}"`;
    });
}

const strip_html_tags = (value: string)=>value.replace(/<[^>]*>/g, '');

const How_it_works_2_card = (props: i_how_it_works_2_card_props)=>{
    const {img_src, title, description, reversed} = props;
    const {theme} = useContext(Settings_context);
    const processed_title = useMemo(()=>
        typeof title==='string'
            ? process_html_classes(title, class_name=>
                theme[class_name] || class_name)
            : null,
    [title, theme]);
    const processed_description = useMemo(()=>
        typeof description==='string'
            ? process_html_classes(description, class_name=>
                theme[class_name] || class_name)
            : null,
    [description, theme]);
    const description_alt = typeof description==='string'
        ? strip_html_tags(description)
        : '';
    return <div className={cn(styles.card, {[styles.card_reversed]:
      reversed})}>
      <div className={styles.card_image_container}>
        <img src={img_src} alt={description_alt} />
      </div>
      <div className={styles.card_text_container}>
        <p className={cn([theme.u2, theme.med, theme.color_n700])}>
          {typeof title==='string'
            ? <span dangerouslySetInnerHTML={{
              __html: processed_title || ''}}/>
            : title}
        </p>
        <p className={theme.p2}>
          {typeof description==='string'
            ? <span dangerouslySetInnerHTML={{
              __html: processed_description || ''}}/>
            : description}
        </p>
      </div>
    </div>;
};

interface i_how_it_works_2_props {
    title?: string;
    cards?: i_how_it_works_2_card_props[];
    cta?: string;
    with_arrow?: boolean;
    render_agreement?: boolean;
    download_vpn?: boolean;
}

export const How_it_works_2 = (props: i_how_it_works_2_props)=>{
    let {title = 'How it works', cards,
        cta = 'Get Hola VPN', with_arrow = true,
        render_agreement = false, download_vpn = false} = props;
    cards = cards && cards.length ? cards : cards_data;
    const cards_with_fallback = cards.map((card, idx)=>{
        if (card.img_src)
            return card;
        const fallback = cards_data[idx]?.img_src;
        return fallback ? {...card, img_src: fallback} : card;
    });
    return <div className={styles.container}>
      <div className={styles.header_container}>
        <h3>{title}</h3>
      </div>
      <div className={styles.cards_container}>
        {cards_with_fallback.map((card, idx)=>
          <div key={idx} className={styles.card_wrapper}>
            <How_it_works_2_card {...card}/>
            {idx<cards.length-1 &&
              <div className={styles.arrow}>
                <Arrow_img />
              </div>}
          </div>)}
      </div>
      <div className={styles.button_container}>
        <Install_btn keep_btn_text text={cta} app_fixed="vpn"
          className={cn('hola_btn hola_btn--md hola_btn--round',
            'hola_btn--txt_colored hola_btn--no_jump',
            !with_arrow && 'hola_btn-no_arrow',
            styles.button)}
          download_vpn={download_vpn}/>
        {!!render_agreement &&
          <div className={styles.agreement_message}><Agreement_message/></div>}
      </div>
    </div>;
};

interface i_how_it_works_2_editor_props {
    value: i_how_it_works_2_props;
    on_change: (new_val: i_how_it_works_2_props)=>void;
}

export const How_it_works_2_editor = (props: i_how_it_works_2_editor_props)=>{
    const {value, on_change} = props;
    const [state, set_state] = useState<i_how_it_works_2_props>(()=>({
        title: value?.title || '',
        cards: value?.cards || [],
        cta: value?.cta || 'Get Hola VPN',
        with_arrow: value?.with_arrow ?? true,
        render_agreement: value?.render_agreement ?? false,
        download_vpn: value?.download_vpn ?? false,
    }));
    const cards = state.cards || [];
    const can_add_card = cards.length < 3;
    useEffect(()=>{
        set_state(c=>({...c, ...value}));
    }, [value]);
    return <Editor
      value={state}
      editors={[
        {key: 'title', label: 'Title', type: 'text'},
        {key: 'cta', label: 'CTA Button Text', type: 'text'},
        {key: 'with_arrow', label: 'CTA Arrow', type: 'boolean'},
        {key: 'render_agreement', label: 'Render agreement message',
          type: 'boolean'},
        {key: 'download_vpn', label: 'Download VPN', type: 'boolean'},
      ]}
      on_change={v=>set_state(v)}
      on_save={()=>on_change(state)}>
      <h4>Cards: </h4>
      {cards.map((card, i)=>{
        const card_title = typeof card.title==='string' && card.title
            ? card.title
            : 'unknown';
        return <Fragment key={i}>
          <Editor
            title={card_title}
            key={i}
            value={card}
            editors={[
              {key: 'img_src', label: 'Image (optional)', type: 'text'},
              {key: 'title', label: 'Title', type: 'text'},
              {key: 'description', label: 'Description', type: 'textarea'},
              {key: 'reversed', label: 'Reversed', type: 'boolean'},
            ]}
            on_change={v=>{
              const items_new = [...cards];
              items_new[i] = v;
              set_state({...state, cards: items_new});
            }}>
          </Editor>
          <Button caption={'Delete Card'} size={'xs'}
            on_click={()=>{
              set_state({...state, cards: cards
                .filter((_, index)=>index!=i)});
            }}/>
        </Fragment>;
      })}
      {can_add_card &&
        <Button caption={'Add Card'} size={'xs'}
          on_click={()=>{
            set_state({...state, cards: [...cards, {
              img_src: '',
              title: '',
              description: '',
              reversed: false,
            }]});
          }}/>
      }
    </Editor>;
};
